import React, { useState, useEffect } from 'react'
import { 
  MapPin, Plus, Edit2, Trash2, Search, Home, Building, 
  School, Heart, Trees, MoreHorizontal, Phone, Map,
  Save, X
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Place {
  id: string
  name: string
  place_type: 'home' | 'school' | 'work' | 'medical' | 'recreation' | 'parent_home' | 'other'
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
  country?: string
  phone_number?: string
  notes?: string
  is_active: boolean
  show_in_dropdown: boolean
  created_at?: string
  updated_at?: string
}

interface User {
  id: string
  display_name: string
  primary_place_id?: string
}

const PLACE_TYPES = [
  { value: 'home', label: 'Home', icon: Home, color: 'text-blue-600' },
  { value: 'parent_home', label: "Parent's Home", icon: Home, color: 'text-indigo-600' },
  { value: 'school', label: 'School', icon: School, color: 'text-green-600' },
  { value: 'work', label: 'Work', icon: Building, color: 'text-gray-600' },
  { value: 'medical', label: 'Medical', icon: Heart, color: 'text-red-600' },
  { value: 'recreation', label: 'Recreation', icon: Trees, color: 'text-purple-600' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'text-gray-500' }
]

export const LocationsManagement: React.FC = () => {
  const [places, setPlaces] = useState<Place[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingPlace, setEditingPlace] = useState<Place | null>(null)
  const [formData, setFormData] = useState<Partial<Place>>({
    name: '',
    place_type: 'home',
    street_address: '',
    city: '',
    state: 'GA',
    zip_code: '',
    phone_number: '',
    notes: '',
    is_active: true,
    show_in_dropdown: true
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [placesData, usersResponse] = await Promise.all([
        apiClient.get('/api/management/places'),
        apiClient.get('/api/users')
      ])
      setPlaces(placesData || [])
      setUsers(usersResponse?.users || [])
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load locations')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      if (editingPlace) {
        await apiClient.put(`/api/management/places/${editingPlace.id}`, formData)
        toast.success('Location updated successfully')
      } else {
        await apiClient.post('/api/management/places', formData)
        toast.success('Location created successfully')
      }
      setShowAddForm(false)
      setEditingPlace(null)
      setFormData({
        name: '',
        place_type: 'home',
        street_address: '',
        city: '',
        state: 'GA',
        zip_code: '',
        phone_number: '',
        notes: '',
        is_active: true,
        show_in_dropdown: true
      })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save location')
    }
  }

  const handleDelete = async (placeId: string) => {
    if (!confirm('Are you sure you want to delete this location?')) return
    
    try {
      await apiClient.delete(`/api/management/places/${placeId}`)
      toast.success('Location deleted successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete location')
    }
  }

  const handleEdit = (place: Place) => {
    setEditingPlace(place)
    setFormData(place)
    setShowAddForm(true)
  }

  const getUsersAtLocation = (placeId: string) => {
    return users.filter(u => u.primary_place_id === placeId)
  }

  const getPlaceIcon = (type: string) => {
    const placeType = PLACE_TYPES.find(t => t.value === type)
    const Icon = placeType?.icon || MapPin
    return <Icon className={`w-5 h-5 ${placeType?.color || 'text-gray-500'}`} />
  }

  const filteredPlaces = places.filter(place => {
    const matchesSearch = 
      place.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.street_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      place.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filterType === 'all' || place.place_type === filterType
    
    return matchesSearch && matchesFilter
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Locations Management</h2>
        <p className="text-gray-600">Manage places and addresses for users, schools, and events</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Types</option>
              {PLACE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => {
              setShowAddForm(true)
              setEditingPlace(null)
              setFormData({
                name: '',
                place_type: 'home',
                street_address: '',
                city: '',
                state: 'GA',
                zip_code: '',
                phone_number: '',
                notes: '',
                is_active: true,
                show_in_dropdown: true
              })
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingPlace ? 'Edit Location' : 'Add New Location'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name *
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Main House"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                value={formData.place_type || 'home'}
                onChange={(e) => setFormData({ ...formData, place_type: e.target.value as Place['place_type'] })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {PLACE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.street_address || ''}
                onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main Street"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Marietta"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="GA"
                maxLength={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ZIP Code
              </label>
              <input
                type="text"
                value={formData.zip_code || ''}
                onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="30064"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone_number || ''}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="555-555-5555"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="Additional information..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.show_in_dropdown ?? true}
                  onChange={(e) => setFormData({ ...formData, show_in_dropdown: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Show in user dropdowns
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Uncheck for event-specific locations like appointments that shouldn't appear in selection lists
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowAddForm(false)
                setEditingPlace(null)
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <X className="w-4 h-4 inline mr-1" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingPlace ? 'Update' : 'Create'} Location
            </button>
          </div>
        </div>
      )}

      {/* Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPlaces.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-lg shadow">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No locations found</p>
            {searchTerm || filterType !== 'all' ? (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setFilterType('all')
                }}
                className="mt-3 text-blue-600 hover:text-blue-700"
              >
                Clear filters
              </button>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-3 text-blue-600 hover:text-blue-700"
              >
                Add your first location
              </button>
            )}
          </div>
        ) : (
          filteredPlaces.map(place => {
            const usersHere = getUsersAtLocation(place.id)
            return (
              <div key={place.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      {getPlaceIcon(place.place_type)}
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {place.name}
                          {!place.show_in_dropdown && (
                            <span className="ml-2 text-xs text-orange-600 font-normal">(Hidden from dropdowns)</span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 capitalize">{place.place_type}</p>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEdit(place)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit location"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(place.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete location"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {(place.street_address || place.city) && (
                    <div className="flex items-start space-x-2 text-sm text-gray-600 mb-2">
                      <Map className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {place.street_address && <p>{place.street_address}</p>}
                        {place.city && (
                          <p>
                            {place.city}{place.state && `, ${place.state}`}
                            {place.zip_code && ` ${place.zip_code}`}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {place.phone_number && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                      <Phone className="w-4 h-4" />
                      <span>{place.phone_number}</span>
                    </div>
                  )}

                  {place.notes && (
                    <p className="text-sm text-gray-500 italic mb-2">{place.notes}</p>
                  )}

                  {usersHere.length > 0 && (
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs text-gray-500 mb-1">Primary location for:</p>
                      <div className="flex flex-wrap gap-1">
                        {usersHere.map(user => (
                          <span
                            key={user.id}
                            className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs"
                          >
                            {user.display_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}